import config from '../config/config';

class ApiService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
  }

  // Método helper para hacer requests
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const requestConfig = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Agregar token JWT real si existe
    const token = localStorage.getItem('auth_token');
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.API_TIMEOUT);

      const response = await fetch(url, {
        ...requestConfig,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      
      // Intentar parsear JSON, pero manejar casos donde no hay contenido
      let data = null;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.text();
          data = text ? JSON.parse(text) : null;
        } catch (e) {}
      }

      if (!response.ok) {
        // Manejo específico de errores comunes
        if (response.status === 409) {
          throw new Error('Este email ya está registrado. ¿Ya tienes una cuenta? Intenta iniciar sesión.');
        } else if (response.status === 400) {
          throw new Error(data?.message || 'Datos inválidos. Verifica la información ingresada.');
        } else if (response.status === 401) {
          throw new Error('Credenciales incorrectas. Verifica tu email y contraseña.');
        } else if (response.status === 404) {
          // Devolver null para indicar que no se encontró (en lugar de lanzar error)
          return null;
        } else if (response.status >= 500) {
          throw new Error('Error del servidor. Intenta más tarde.');
        } else {
          throw new Error(data?.message || config.ERROR_MESSAGES.API_ERROR);
        }
      }

      return data;
    } catch (error) {
      
      
      if (error.name === 'AbortError') {
        throw new Error(config.ERROR_MESSAGES.TIMEOUT_ERROR);
      }
      
      if (!navigator.onLine) {
        throw new Error(config.ERROR_MESSAGES.NETWORK_ERROR);
      }
      
      // Si es un error de conexión al servidor
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
        throw new Error('No se puede conectar al servidor. Verifica que el backend esté ejecutándose.');
      }
      
      throw error;
    }
  }

  // Autenticación
  async register(userData) {
    // Calcular fecha de nacimiento desde la edad
    const currentYear = new Date().getFullYear();
    const birthYear = currentYear - userData.age;
    const birthDate = `${birthYear}-01-01`;

    // Mapear preferencias de sexo
    let matchSex = [];
    if (userData.sexoBusca) {
      if (userData.sexoBusca.includes('Hombre') && userData.sexoBusca.includes('Mujer')) {
        matchSex = ['male', 'female'];
      } else if (userData.sexoBusca.includes('Hombre')) {
        matchSex = ['male'];
      } else if (userData.sexoBusca.includes('Mujer')) {
        matchSex = ['female'];
      }
    } else {
      // Fallback para compatibilidad con el campo anterior
      matchSex = userData.lookingFor === 'Ambos' ? ['male', 'female'] : 
                 userData.lookingFor === 'Hombre' ? ['male'] : 
                 userData.lookingFor === 'Mujer' ? ['female'] : 
                 userData.gender === 'Hombre' ? ['female'] : ['male'];
    }

    const requestBody = {
      email: userData.email,
      username: userData.username,
      password: userData.password,
      birthDate: birthDate,
      sex: userData.soyGender === 'Hombre' ? 'male' : userData.soyGender === 'Mujer' ? 'female' : 'male',
      matchSex: matchSex
    };

    // Agregar campos opcionales solo si están presentes
    if (userData.firstName) requestBody.firstName = userData.firstName;
    if (userData.location) requestBody.location = userData.location;
    if (userData.bio) requestBody.bio = userData.bio;
    if (userData.estadoCivil) requestBody.estadoCivil = userData.estadoCivil;
    if (userData.sexoBusca) requestBody.sexoBusca = userData.sexoBusca;
    if (userData.descripcionFisica) requestBody.descripcionFisica = userData.descripcionFisica;
    if (userData.educacion) requestBody.educacion = userData.educacion;
    if (userData.edad) requestBody.edad = userData.edad;
    if (userData.ocupacion) requestBody.ocupacion = userData.ocupacion;
    if (userData.tipoPersonaBusca) requestBody.tipoPersonaBusca = userData.tipoPersonaBusca;

    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    // Retornar la respuesta completa del backend
    return {
      success: true,
      message: response.message || 'Usuario registrado exitosamente',
      user: response.user,
      token: response.token
    };
  }

  async login(identifier, password, identifierType = 'email') {
    const requestBody = { password };
    
    // Usar solo el campo que corresponde al tipo de identificador
    if (identifierType === 'email') {
      requestBody.email = identifier;
    } else {
      requestBody.username = identifier;
      // Asegurarse de no incluir el campo email cuando se usa username
      delete requestBody.email;
    }
    
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });

    // Guardar token y userId
    if (response.token) {
      localStorage.setItem('auth_token', response.token);
    }
    
    if (response.user && response.user.id) {
      localStorage.setItem('userId', response.user.id);
    }

    return response;
  }

  async logout() {
    try {
      // El backend obtiene el userId del token JWT decodificado
      // Solo necesitamos enviar el token en el header (ya se hace automáticamente)
      await this.request('/auth/logout', {
        method: 'POST'
        // No se envía body, el backend obtiene userId del token
      });
    } catch (error) {
      // Continuar con logout local aunque falle el servidor
    }
    
    // Limpiar datos locales
    localStorage.removeItem('auth_token');
    localStorage.removeItem('userId');
    
    return { success: true, message: 'Logout exitoso' };
  }

  // Recuperación de contraseña
  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  async resetPassword(token, password) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password })
    });
  }

  async verifyResetToken(token) {
    return this.request(`/auth/verify-reset-token/${token}`);
  }

  // Usuarios
  async getUsers() {
    return this.request('/users');
  }

  async getUserById(id) {
    // Always include the auth token to ensure admin access works
    const token = localStorage.getItem('auth_token');
    return this.request(`/users/${id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  async getUserByUsername(username) {
    return this.request(`/users/username/${username}`);
  }

  async updateUser(id, userData) {
    // Calcular fecha de nacimiento desde la edad si se proporciona
    let birthDate = null;
    if (userData.age) {
      const currentYear = new Date().getFullYear();
      const birthYear = currentYear - userData.age;
      birthDate = `${birthYear}-05-15`; // Usar día 15 como default
    }

    // Mapear preferencias de sexo si se proporciona sexoBusca
    let matchSex = [];
    if (userData.sexoBusca) {
      if (userData.sexoBusca.includes('Hombre') && userData.sexoBusca.includes('Mujer')) {
        matchSex = ['male', 'female'];
      } else if (userData.sexoBusca.includes('Hombre')) {
        matchSex = ['male'];
      } else if (userData.sexoBusca.includes('Mujer')) {
        matchSex = ['female'];
      }
    }

    const requestBody = {};

    // Agregar campos solo si están presentes en userData
    if (userData.name) requestBody.firstName = userData.name;
    if (birthDate) requestBody.birthDate = birthDate;
    if (userData.gender) requestBody.sex = userData.gender === 'Hombre' ? 'male' : userData.gender === 'Mujer' ? 'female' : 'male';
    if (matchSex.length > 0) requestBody.matchSex = matchSex;
    if (userData.state) requestBody.location = userData.state;
    if (userData.municipio !== undefined) requestBody.municipio = userData.municipio;
    if (userData.description) requestBody.bio = userData.description;
    if (userData.estadoCivil) requestBody.estadoCivil = userData.estadoCivil;
    if (userData.sexoBusca) requestBody.sexoBusca = userData.sexoBusca;
    if (userData.descripcionFisica) requestBody.descripcionFisica = userData.descripcionFisica;
    if (userData.educacion) requestBody.educacion = userData.educacion;
    if (userData.ocupacion) requestBody.ocupacion = userData.ocupacion;
    if (userData.tipoPersonaBusca) requestBody.tipoPersonaBusca = userData.tipoPersonaBusca;

    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(requestBody)
    });
  }

  // Cambiar contraseña (usuario autenticado)
  async changeUserPassword(userId, oldPassword, newPassword) {
    return this.request(`/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ oldPassword, newPassword })
    });
  }

  // Subida de imagen de perfil
  async uploadAvatar(userId, file) {
    const formData = new FormData();
    formData.append('avatar', file);

    return this.request(`/users/${userId}/avatar`, {
      method: 'POST',
      body: formData,
      headers: {} // Eliminar Content-Type para que fetch lo maneje automáticamente
    });
  }

  // Eliminar imagen de perfil
  async deleteAvatar(userId) {
    return this.request(`/users/${userId}/avatar`, {
      method: 'DELETE'
    });
  }

  // Búsqueda
  async searchUsers(filters = {}, page = 1, limit = 20) {
    const params = [];
    
    // Siempre agregar paginación para tener control del límite
    params.push(`page=${page}`);
    params.push(`limit=${limit}`);
    
    // Filtros de género - solo male, female, couple permitidos
    if (filters.lookingFor && filters.lookingFor !== 'Ambos') {
      params.push(`sex=${filters.lookingFor === 'Hombre' ? 'male' : 'female'}`);
    }
    
    // Filtros de edad - solo enviar si son diferentes a los valores por defecto
    if (filters.minAge && filters.minAge !== 18) {
      params.push(`ageMin=${filters.minAge}`);
    }
    
    if (filters.maxAge && filters.maxAge !== 100) {
      params.push(`ageMax=${filters.maxAge}`);
    }
    
    // Filtro de ubicación - dejar espacios como están
    if (filters.state) {
      params.push(`location=${filters.state}`);
    }
    
    // Filtro de usuarios online
    if (filters.onlineOnly) {
      params.push('online=true');
    }
    
    // Filtro de usuarios con foto
    if (filters.hasPhoto) {
      params.push('hasPhoto=true');
    }
    
    // Filtro "Yo soy X buscando Y" - bidireccional, dejar espacios como están
    if (filters.sexoBusca) {
      params.push(`sexoBusca=${filters.sexoBusca}`);
    }

    // Filtro de búsqueda por texto (username, nombre, etc.)
    if (filters.search) {
      params.push(`search=${encodeURIComponent(filters.search)}`);
    }

    const queryString = params.join('&');
    
    return this.request(`/search${queryString ? `?${queryString}` : ''}`);
  }

  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // Obtener usuarios en línea para landing page
  async getOnlineUsers() {
    return this.request('/users/online');
  }

  // ===== MENSAJERÍA =====
  
  // Enviar mensaje
  async sendMessage(receiverId, message) {
    return await this.request('/messages', {
      method: 'POST',
      body: JSON.stringify({
        receiverId,
        message
      })
    });
  }

  // Obtener conversaciones usando el nuevo endpoint de rango
  async getConversations(startId = 1, endId = 20, userId = null) {
    
    
    // Si se proporciona userId, usar endpoint de admin (para admin viendo conversaciones de otro usuario)
    if (userId) {
      return await this.getAdminUserConversations(userId, startId, endId);
    }
    
    // Endpoint de rango para usuarios regulares
    const url = `/messages/conversations/range?startId=${startId}&endId=${endId}`;
    
    return await this.request(url);
  }

  // Obtener mensajes con un usuario específico
  async getMessages(userId, page = 1, limit = 10) {
    return await this.request(`/messages/${userId}?page=${page}&limit=${limit}`);
  }

  // ===== SISTEMA DE BLOQUEO =====
  
  // Bloquear usuario
  async blockUser(userId) {
    return await this.request(`/messages/block/${userId}`, {
      method: 'POST'
    });
  }

  // Desbloquear usuario
  async unblockUser(userId) {
    return await this.request(`/messages/block/${userId}`, {
      method: 'DELETE'
    });
  }

  // ===== FUNCIONALIDADES DE ADMIN =====
  
  // Eliminar usuario (solo admin)
  async deleteUser(userId) {
    return await this.request(`/users/${userId}`, {
      method: 'DELETE'
    });
  }

  // Editar usuario (solo admin)
  async editUser(userId, userData) {
    return await this.request(`/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  }

  // Cambiar contraseña de usuario (solo admin)
  async adminUpdateUserPassword(userId, password) {
    return await this.request(`/admin/users/${userId}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ password })
    });
  }

  // ===== SISTEMA DE REPORTES =====
  
  // Reportar usuario
  async reportUser(username, reason, description = '') {
    return await this.request('/reports', {
      method: 'POST',
      body: JSON.stringify({
        username,
        reason,
        description
      })
    });
  }

  // Ver todos los reportes (solo admin)
  async getAllReports(resolved = null, page = 1, limit = 20) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    
    if (resolved !== null) {
      params.append('resolved', resolved.toString());
    }
    
    return await this.request(`/reports?${params.toString()}`);
  }

  // Ver reporte específico (solo admin)
  async getReport(reportId) {
    return await this.request(`/reports/${reportId}`);
  }

  // Marcar reporte como resuelto (solo admin)
  async resolveReport(reportId) {
    return await this.request(`/reports/${reportId}/resolve`, {
      method: 'PATCH'
    });
  }

  // ===== ADMIN: REVISAR CONVERSACIONES DE USUARIOS =====
  
  // Ver todas las conversaciones de un usuario (solo admin)
  async getAdminUserConversations(userId, page = 1, limit = 20) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    
    return await this.request(`/admin/users/${userId}/conversations?${params.toString()}`);
  }

  // Ver mensajes específicos entre dos usuarios (solo admin)
  async getAdminConversationMessages(userId, otherUserId, page = 1, limit = 50) {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('limit', limit);
    
    return await this.request(`/admin/conversations/${userId}/${otherUserId}?${params.toString()}`);
  }
}

const apiService = new ApiService();
export default apiService;
